/**
 * Track Record API
 *
 * Mock track record service for challenge documentation.
 */

import type {
  TrackRecord,
  TrackRecordEntry,
  TrackRecordEntryMedia,
  TrackRecordComment,
  TrackRecordEntryReaction,
  EntryReactionEmoji,
  EntryReactionSummary,
  UserChallenge,
  Challenge,
  CommunityAssignment,
  Community,
  CommunityMembership,
  CommunityStudentProgress,
  PendingReview,
  StudentTrackRecordSummary,
  TrackRecordFeedEntry,
  RejectionReasonTemplate,
  ApiResponse,
  UUID,
  User,
} from '../types'
import { mockDb, delay } from './mockDb'
import { success, error } from './client'

// Session key (shared)
const SESSION_KEY = 'docurious-session'

function getCurrentUserId(): string | null {
  const stored = localStorage.getItem(SESSION_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored).userId
  } catch {
    return null
  }
}

/**
 * Track Record API
 */
export const trackRecordApi = {
  /**
   * Get track record for a challenge (by challengeId)
   * Looks up the UserChallenge for the current user and returns the track record if it exists
   */
  async getTrackRecord(challengeId: UUID): Promise<ApiResponse<TrackRecord | null>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Find the UserChallenge for this user and challenge
    const userChallenge = mockDb.findOne<UserChallenge>(
      'userChallenges',
      (uc) => uc.userId === userId && uc.challengeId === challengeId
    )

    if (!userChallenge) {
      // No UserChallenge exists - user hasn't started this challenge yet
      return success(null)
    }

    const trackRecord = mockDb.findOne<TrackRecord>(
      'trackRecords',
      (tr) => tr.userChallengeId === userChallenge.id
    )

    if (trackRecord) {
      // Populate entries
      trackRecord.entries = mockDb.findWhere<TrackRecordEntry>(
        'trackRecordEntries',
        (e) => e.trackRecordId === trackRecord.id
      ).sort((a, b) => a.displayOrder - b.displayOrder)

      // Populate media and reactions for each entry
      trackRecord.entries = trackRecord.entries.map((entry) => {
        const media = mockDb.findWhere<TrackRecordEntryMedia>(
          'trackRecordMedia',
          (m) => m.entryId === entry.id
        ).sort((a, b) => a.displayOrder - b.displayOrder)

        // Populate reactions
        const reactions = mockDb.findWhere<TrackRecordEntryReaction>(
          'entryReactions',
          (r) => r.entryId === entry.id
        )
        const summary: EntryReactionSummary = {}
        const userReactions: EntryReactionEmoji[] = []
        for (const r of reactions) {
          summary[r.emoji] = (summary[r.emoji] || 0) + 1
          if (r.userId === userId) userReactions.push(r.emoji)
        }

        return {
          ...entry,
          media,
          reactions: summary,
          userReactions,
        }
      })

      // Populate challenge
      trackRecord.challenge = mockDb.findById<Challenge>('challenges', trackRecord.challengeId) ?? undefined

      // Populate userChallenge (includes community assignment info)
      trackRecord.userChallenge = userChallenge
    }

    return success(trackRecord)
  },

  /**
   * Create track record for a challenge (by challengeId)
   * Creates the UserChallenge if needed, then creates the track record
   */
  async createTrackRecord(challengeId: UUID): Promise<ApiResponse<TrackRecord>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Check if challenge exists
    const challenge = mockDb.findById<Challenge>('challenges', challengeId)
    if (!challenge) {
      return error('Challenge not found')
    }

    // Find or create the UserChallenge
    let userChallenge = mockDb.findOne<UserChallenge>(
      'userChallenges',
      (uc) => uc.userId === userId && uc.challengeId === challengeId
    )

    if (!userChallenge) {
      // Check if there's a community assignment for this challenge
      // First, find communities the user is a member of
      const userMemberships = mockDb.findWhere<CommunityMembership>(
        'communityMemberships',
        (m) => m.userId === userId && m.status === 'active'
      )
      const userCommunityIds = userMemberships.map(m => m.communityId)

      // Find any active assignment for this challenge in user's communities
      const assignment = mockDb.findOne<CommunityAssignment>(
        'communityAssignments',
        (a) => a.challengeId === challengeId &&
               a.status === 'active' &&
               userCommunityIds.includes(a.communityId)
      )

      let source: 'self' | 'class_assignment' = 'self'
      let communityAssignmentId: string | undefined
      let communityId: string | undefined
      let communityName: string | undefined

      if (assignment) {
        source = 'class_assignment'
        communityAssignmentId = assignment.id
        communityId = assignment.communityId
        // Get community name
        const community = mockDb.findById<Community>('communities', assignment.communityId)
        communityName = community?.name
      }

      // Create the UserChallenge (equivalent to "starting" the challenge)
      userChallenge = mockDb.insert<UserChallenge>('userChallenges', {
        userId,
        challengeId,
        status: 'in_progress',
        startedAt: new Date().toISOString(),
        source,
        communityAssignmentId,
        communityId,
        communityName,
        completionNumber: 1,
        verificationAttempts: 0,
        maxAttempts: 3,
      })
    }

    // Check if track record already exists
    const existing = mockDb.findOne<TrackRecord>(
      'trackRecords',
      (tr) => tr.userChallengeId === userChallenge!.id
    )

    if (existing) {
      existing.entries = []
      existing.userChallenge = userChallenge
      return success(existing)
    }

    const trackRecord = mockDb.insert<TrackRecord>('trackRecords', {
      userChallengeId: userChallenge.id,
      userId,
      challengeId,
      status: 'draft',
      storageUsedBytes: 0,
      sharingScope: 'private',
      verificationAttempts: 0,
      escalated: false,
    })

    trackRecord.entries = []
    trackRecord.userChallenge = userChallenge

    return success(trackRecord)
  },

  /**
   * Add an entry to a track record
   */
  async addEntry(
    trackRecordId: UUID,
    data: {
      textContent?: string
      milestoneId?: UUID
    }
  ): Promise<ApiResponse<TrackRecordEntry>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const trackRecord = mockDb.findById<TrackRecord>('trackRecords', trackRecordId)
    if (!trackRecord) {
      return error('Track record not found')
    }

    if (trackRecord.userId !== userId) {
      return error('Not authorized')
    }

    if (trackRecord.status === 'verified') {
      return error('Cannot add entries to verified track record')
    }

    // Get current max display order
    const existingEntries = mockDb.findWhere<TrackRecordEntry>(
      'trackRecordEntries',
      (e) => e.trackRecordId === trackRecordId
    )
    const maxOrder = Math.max(0, ...existingEntries.map((e) => e.displayOrder))

    const entry = mockDb.insert<TrackRecordEntry>('trackRecordEntries', {
      trackRecordId,
      textContent: data.textContent,
      milestoneId: data.milestoneId,
      displayOrder: maxOrder + 1,
      editedAfterVerification: false,
      thumbsUpCount: 0,
      commentCount: 0,
    })

    entry.media = []

    return success(entry)
  },

  /**
   * Update an entry
   */
  async updateEntry(
    entryId: UUID,
    data: { textContent?: string }
  ): Promise<ApiResponse<TrackRecordEntry>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const entry = mockDb.findById<TrackRecordEntry>('trackRecordEntries', entryId)
    if (!entry) {
      return error('Entry not found')
    }

    const trackRecord = mockDb.findById<TrackRecord>('trackRecords', entry.trackRecordId)
    if (!trackRecord || trackRecord.userId !== userId) {
      return error('Not authorized')
    }

    const wasVerified = trackRecord.status === 'verified'

    const updated = mockDb.update<TrackRecordEntry>('trackRecordEntries', entryId, {
      textContent: data.textContent,
      editedAfterVerification: wasVerified,
      editedAt: wasVerified ? new Date().toISOString() : undefined,
    })

    if (!updated) {
      return error('Failed to update entry')
    }

    return success(updated)
  },

  /**
   * Delete an entry
   */
  async deleteEntry(entryId: UUID): Promise<ApiResponse<void>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const entry = mockDb.findById<TrackRecordEntry>('trackRecordEntries', entryId)
    if (!entry) {
      return error('Entry not found')
    }

    const trackRecord = mockDb.findById<TrackRecord>('trackRecords', entry.trackRecordId)
    if (!trackRecord || trackRecord.userId !== userId) {
      return error('Not authorized')
    }

    if (trackRecord.status === 'verified') {
      return error('Cannot delete entries from verified track record')
    }

    // Delete media first
    const media = mockDb.findWhere<TrackRecordEntryMedia>(
      'trackRecordMedia',
      (m) => m.entryId === entryId
    )
    media.forEach((m) => mockDb.delete('trackRecordMedia', m.id))

    // Delete entry
    mockDb.delete('trackRecordEntries', entryId)

    return success(undefined)
  },

  /**
   * Add media to an entry (simulated - would normally handle file upload)
   */
  async addMedia(
    entryId: UUID,
    data: {
      mediaType: 'image' | 'video_link' | 'audio_link' | 'document'
      url: string
      fileSizeBytes?: number
      originalFilename?: string
    }
  ): Promise<ApiResponse<TrackRecordEntryMedia>> {
    await delay(150)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const entry = mockDb.findById<TrackRecordEntry>('trackRecordEntries', entryId)
    if (!entry) {
      return error('Entry not found')
    }

    const trackRecord = mockDb.findById<TrackRecord>('trackRecords', entry.trackRecordId)
    if (!trackRecord || trackRecord.userId !== userId) {
      return error('Not authorized')
    }

    // Get current max display order
    const existingMedia = mockDb.findWhere<TrackRecordEntryMedia>(
      'trackRecordMedia',
      (m) => m.entryId === entryId
    )
    const maxOrder = Math.max(0, ...existingMedia.map((m) => m.displayOrder))

    const media = mockDb.insert<TrackRecordEntryMedia>('trackRecordMedia', {
      entryId,
      mediaType: data.mediaType,
      url: data.url,
      fileSizeBytes: data.fileSizeBytes,
      originalFilename: data.originalFilename,
      displayOrder: maxOrder + 1,
    })

    // Update storage used
    if (data.fileSizeBytes) {
      mockDb.update<TrackRecord>('trackRecords', trackRecord.id, {
        storageUsedBytes: trackRecord.storageUsedBytes + data.fileSizeBytes,
      })
    }

    return success(media)
  },

  /**
   * Submit track record for verification
   */
  async submitForVerification(trackRecordId: UUID): Promise<ApiResponse<TrackRecord>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const trackRecord = mockDb.findById<TrackRecord>('trackRecords', trackRecordId)
    if (!trackRecord) {
      return error('Track record not found')
    }

    if (trackRecord.userId !== userId) {
      return error('Not authorized')
    }

    if (trackRecord.status !== 'draft' && trackRecord.status !== 'rejected') {
      return error('Track record cannot be submitted in current state')
    }

    // Check that there's at least one entry
    const entries = mockDb.findWhere<TrackRecordEntry>(
      'trackRecordEntries',
      (e) => e.trackRecordId === trackRecordId
    )

    if (entries.length === 0) {
      return error('Track record must have at least one entry')
    }

    // Check verification attempts (max 3)
    const currentAttempts = trackRecord.verificationAttempts || 0
    if (currentAttempts >= 3) {
      return error('Maximum verification attempts reached. Please contact support.')
    }

    // Calculate SLA deadline (72 hours from now)
    const now = new Date()
    const slaDeadline = new Date(now.getTime() + 72 * 60 * 60 * 1000) // 72 hours

    // Update track record with SLA tracking
    const updated = mockDb.update<TrackRecord>('trackRecords', trackRecordId, {
      status: 'submitted',
      submittedAt: now.toISOString(),
      submittedForReviewAt: now.toISOString(),
      slaDeadline: slaDeadline.toISOString(),
      verificationAttempts: currentAttempts + 1,
      escalated: false,
    })

    // Update user challenge
    const userChallenge = mockDb.findById<UserChallenge>(
      'userChallenges',
      trackRecord.userChallengeId
    )
    if (userChallenge) {
      mockDb.update<UserChallenge>('userChallenges', userChallenge.id, {
        status: 'submitted',
        submittedAt: new Date().toISOString(),
        verificationAttempts: userChallenge.verificationAttempts + 1,
      })

      // Update community student progress if this is a class assignment
      if (userChallenge.communityAssignmentId) {
        const progress = mockDb.findOne<CommunityStudentProgress>(
          'studentAssignmentProgress',
          (p) => p.assignmentId === userChallenge.communityAssignmentId && p.userId === userId
        )
        if (progress) {
          mockDb.update<CommunityStudentProgress>('studentAssignmentProgress', progress.id, {
            status: 'submitted',
            submittedAt: new Date().toISOString(),
            trackRecordId: trackRecordId,
          })
        }
      }
    }

    if (!updated) {
      return error('Failed to submit track record')
    }

    return success(updated)
  },

  /**
   * Update sharing scope
   */
  async updateSharingScope(
    trackRecordId: UUID,
    scope: 'private' | 'communities' | 'individuals' | 'public'
  ): Promise<ApiResponse<TrackRecord>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const trackRecord = mockDb.findById<TrackRecord>('trackRecords', trackRecordId)
    if (!trackRecord) {
      return error('Track record not found')
    }

    if (trackRecord.userId !== userId) {
      return error('Not authorized')
    }

    const updated = mockDb.update<TrackRecord>('trackRecords', trackRecordId, {
      sharingScope: scope,
    })

    if (!updated) {
      return error('Failed to update sharing scope')
    }

    return success(updated)
  },

  /**
   * Get public track records for a challenge (gallery)
   */
  async getChallengeGallery(
    challengeId: UUID,
    page = 1,
    limit = 12
  ): Promise<ApiResponse<TrackRecord[]>> {
    await delay(100)

    const trackRecords = mockDb.findWhere<TrackRecord>(
      'trackRecords',
      (tr) =>
        tr.challengeId === challengeId &&
        tr.status === 'verified' &&
        tr.sharingScope === 'public'
    )

    // Paginate
    const start = (page - 1) * limit
    const paginatedRecords = trackRecords.slice(start, start + limit)

    // Populate basic info
    const withEntries = paginatedRecords.map((tr) => ({
      ...tr,
      entries: mockDb.findWhere<TrackRecordEntry>(
        'trackRecordEntries',
        (e) => e.trackRecordId === tr.id
      ).slice(0, 1), // Just first entry for preview
    }))

    return success(withEntries)
  },

  // ============================================================================
  // Comments
  // ============================================================================

  /**
   * Get comments for an entry
   */
  async getEntryComments(entryId: UUID): Promise<ApiResponse<TrackRecordComment[]>> {
    await delay(50)

    const comments = mockDb.findWhere<TrackRecordComment>(
      'trackRecordComments',
      (c) => c.entryId === entryId
    )

    // Sort by date, newest first
    comments.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

    return success(comments)
  },

  /**
   * Add a comment to an entry
   */
  async addComment(
    entryId: UUID,
    data: { content: string; parentCommentId?: UUID }
  ): Promise<ApiResponse<TrackRecordComment>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const entry = mockDb.findById<TrackRecordEntry>('trackRecordEntries', entryId)
    if (!entry) {
      return error('Entry not found')
    }

    // Get user info for author
    const users = mockDb.getAll<{ id: string; displayName: string }>('users')
    const user = users.find((u) => u.id === userId)

    const comment = mockDb.insert<TrackRecordComment>('trackRecordComments', {
      entryId,
      authorUserId: userId,
      parentCommentId: data.parentCommentId,
      content: data.content,
      visibility: 'public',
      isInstructorFeedback: false,
      edited: false,
      editDeadline: new Date(Date.now() + 15 * 60 * 1000).toISOString(), // 15 min edit window
      author: {
        id: userId,
        displayName: user?.displayName ?? 'Unknown User',
      },
    })

    // Update comment count on entry
    mockDb.update<TrackRecordEntry>('trackRecordEntries', entryId, {
      commentCount: entry.commentCount + 1,
    })

    return success(comment)
  },

  /**
   * Update a comment (within edit window)
   */
  async updateComment(
    commentId: UUID,
    data: { content: string }
  ): Promise<ApiResponse<TrackRecordComment>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const comment = mockDb.findById<TrackRecordComment>('trackRecordComments', commentId)
    if (!comment) {
      return error('Comment not found')
    }

    if (comment.authorUserId !== userId) {
      return error('Not authorized')
    }

    // Check edit deadline
    if (new Date(comment.editDeadline) < new Date()) {
      return error('Edit window has expired')
    }

    const updated = mockDb.update<TrackRecordComment>('trackRecordComments', commentId, {
      content: data.content,
      edited: true,
    })

    if (!updated) {
      return error('Failed to update comment')
    }

    return success(updated)
  },

  /**
   * Delete a comment
   */
  async deleteComment(commentId: UUID): Promise<ApiResponse<void>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const comment = mockDb.findById<TrackRecordComment>('trackRecordComments', commentId)
    if (!comment) {
      return error('Comment not found')
    }

    if (comment.authorUserId !== userId) {
      return error('Not authorized')
    }

    // Update comment count on entry
    const entry = mockDb.findById<TrackRecordEntry>('trackRecordEntries', comment.entryId)
    if (entry) {
      mockDb.update<TrackRecordEntry>('trackRecordEntries', comment.entryId, {
        commentCount: Math.max(0, entry.commentCount - 1),
      })
    }

    mockDb.delete('trackRecordComments', commentId)

    return success(undefined)
  },

  // ============================================================================
  // Teacher Review & Verification
  // ============================================================================

  /**
   * Get all student track records for a community's assignments.
   * Returns summaries for every track record (draft, submitted, verified, rejected)
   * so teachers can see the full picture on the community page.
   */
  async getCommunityStudentTrackRecords(communityId: UUID): Promise<ApiResponse<StudentTrackRecordSummary[]>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) return error('Not authenticated')

    // Verify the user is a teacher/admin in this community
    const membership = mockDb.findOne<CommunityMembership>(
      'communityMemberships',
      (m) => m.communityId === communityId && m.userId === userId && ['owner', 'creator', 'admin', 'moderator'].includes(m.role)
    )
    if (!membership) return error('Not authorized')

    const community = mockDb.findById<Community>('communities', communityId)
    if (!community) return error('Community not found')

    // Get all assignments for this community
    const assignments = mockDb.findWhere<CommunityAssignment>(
      'communityAssignments',
      (a) => a.communityId === communityId
    )
    const assignmentChallengeIds = assignments.map(a => a.challengeId)

    // Get all track records for challenges assigned in this community
    const trackRecords = mockDb.findWhere<TrackRecord>(
      'trackRecords',
      (tr) => assignmentChallengeIds.includes(tr.challengeId)
    )

    // Filter to only students who are community members with class_assignment user challenges
    const summaries: StudentTrackRecordSummary[] = []

    for (const tr of trackRecords) {
      const userChallenge = mockDb.findById<UserChallenge>('userChallenges', tr.userChallengeId)
      if (!userChallenge || userChallenge.source !== 'class_assignment') continue
      if (userChallenge.communityId !== communityId) continue

      const student = mockDb.findById<User>('users', tr.userId)
      if (!student) continue

      const challenge = mockDb.findById<Challenge>('challenges', tr.challengeId)
      if (!challenge) continue

      const assignment = assignments.find(a => a.challengeId === tr.challengeId)

      const entries = mockDb.findWhere<TrackRecordEntry>(
        'trackRecordEntries',
        (e) => e.trackRecordId === tr.id
      )

      summaries.push({
        trackRecordId: tr.id,
        userId: tr.userId,
        challengeId: tr.challengeId,
        communityId,
        communityName: community.name,
        assignmentId: assignment?.id,
        student: {
          id: student.id,
          displayName: student.displayName,
          avatarUrl: student.avatarUrl,
        },
        challenge: {
          id: challenge.id,
          title: challenge.title,
          coverImageUrl: challenge.coverImageUrl,
        },
        submittedAt: tr.submittedAt || tr.updatedAt,
        updatedAt: tr.updatedAt,
        verificationAttempts: userChallenge.verificationAttempts,
        entryCount: entries.length,
        status: tr.status,
      })
    }

    // Sort: submitted first, then draft/in-progress, then verified, then rejected
    const statusOrder: Record<string, number> = { submitted: 0, draft: 1, rejected: 2, verified: 3, unverified: 4 }
    summaries.sort((a, b) => (statusOrder[a.status] ?? 5) - (statusOrder[b.status] ?? 5))

    return success(summaries)
  },

  /**
   * Get a chronological feed of individual track record entries for a community.
   * Each entry is enriched with student and challenge context.
   */
  async getCommunityTrackRecordFeed(communityId: UUID): Promise<ApiResponse<TrackRecordFeedEntry[]>> {
    await delay(80)

    const userId = getCurrentUserId()
    if (!userId) return error('Not authenticated')

    // Any community member can view the feed
    const membership = mockDb.findOne<CommunityMembership>(
      'communityMemberships',
      (m) => m.communityId === communityId && m.userId === userId
    )
    if (!membership) return error('Not a member')

    const community = mockDb.findById<Community>('communities', communityId)
    if (!community) return error('Community not found')

    // Get assignments for this community
    const assignments = mockDb.findWhere<CommunityAssignment>(
      'communityAssignments',
      (a) => a.communityId === communityId
    )
    const assignmentChallengeIds = assignments.map(a => a.challengeId)

    // Get all track records for these challenges from class assignments
    const trackRecords = mockDb.findWhere<TrackRecord>(
      'trackRecords',
      (tr) => assignmentChallengeIds.includes(tr.challengeId)
    )

    const feedEntries: TrackRecordFeedEntry[] = []

    for (const tr of trackRecords) {
      const userChallenge = mockDb.findById<UserChallenge>('userChallenges', tr.userChallengeId)
      if (!userChallenge || userChallenge.source !== 'class_assignment') continue
      if (userChallenge.communityId !== communityId) continue

      const student = mockDb.findById<User>('users', tr.userId)
      if (!student) continue

      const challenge = mockDb.findById<Challenge>('challenges', tr.challengeId)
      if (!challenge) continue

      // Get all entries for this track record
      const entries = mockDb.findWhere<TrackRecordEntry>(
        'trackRecordEntries',
        (e) => e.trackRecordId === tr.id
      )

      // Populate media and reactions for each entry
      for (const entry of entries) {
        const media = mockDb.findWhere<TrackRecordEntryMedia>(
          'trackRecordMedia',
          (m) => m.entryId === entry.id
        )
        entry.media = media

        // Populate reactions
        const reactions = mockDb.findWhere<TrackRecordEntryReaction>(
          'entryReactions',
          (r) => r.entryId === entry.id
        )
        const summary: EntryReactionSummary = {}
        const userReactions: EntryReactionEmoji[] = []
        for (const r of reactions) {
          summary[r.emoji] = (summary[r.emoji] || 0) + 1
          if (r.userId === userId) userReactions.push(r.emoji)
        }
        entry.reactions = summary
        entry.userReactions = userReactions

        feedEntries.push({
          entry,
          student: {
            id: student.id,
            displayName: student.displayName,
            avatarUrl: student.avatarUrl,
          },
          challenge: {
            id: challenge.id,
            title: challenge.title,
            coverImageUrl: challenge.coverImageUrl,
          },
          trackRecordId: tr.id,
          trackRecordStatus: tr.status,
        })
      }
    }

    // Sort newest first
    feedEntries.sort((a, b) =>
      new Date(b.entry.createdAt).getTime() - new Date(a.entry.createdAt).getTime()
    )

    return success(feedEntries)
  },

  /**
   * Get pending reviews for a teacher's communities
   * Returns track records with status='submitted' from communities where the user is a teacher/owner
   */
  async getPendingReviewsForTeacher(communityId?: UUID): Promise<ApiResponse<PendingReview[]>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Get communities where user is teacher/owner
    const teacherMemberships = mockDb.findWhere<CommunityMembership>(
      'communityMemberships',
      (m) => m.userId === userId && m.status === 'active' && ['owner', 'creator', 'admin', 'moderator'].includes(m.role)
    )
    const teacherCommunityIds = teacherMemberships.map(m => m.communityId)

    // Filter by specific community if provided
    const relevantCommunityIds = communityId
      ? teacherCommunityIds.filter(id => id === communityId)
      : teacherCommunityIds

    if (relevantCommunityIds.length === 0) {
      return success([])
    }

    // Get active assignments for these communities
    const assignments = mockDb.findWhere<CommunityAssignment>(
      'communityAssignments',
      (a) => relevantCommunityIds.includes(a.communityId) && a.status === 'active'
    )
    const assignmentChallengeIds = assignments.map(a => a.challengeId)

    // Get submitted track records for challenges in these assignments
    const submittedTrackRecords = mockDb.findWhere<TrackRecord>(
      'trackRecords',
      (tr) => tr.status === 'submitted' && assignmentChallengeIds.includes(tr.challengeId)
    )

    // Build pending reviews
    const pendingReviews: PendingReview[] = []

    for (const tr of submittedTrackRecords) {
      // Get the user challenge to check if it's from a class assignment
      const userChallenge = mockDb.findById<UserChallenge>('userChallenges', tr.userChallengeId)
      if (!userChallenge || userChallenge.source !== 'class_assignment') {
        continue // Skip non-assignment track records
      }

      // Get the assignment
      const assignment = assignments.find(a => a.challengeId === tr.challengeId && a.communityId === userChallenge.communityId)
      if (!assignment) continue

      // Get community
      const community = mockDb.findById<Community>('communities', assignment.communityId)
      if (!community) continue

      // Get student
      const student = mockDb.findById<User>('users', tr.userId)
      if (!student) continue

      // Get challenge
      const challenge = mockDb.findById<Challenge>('challenges', tr.challengeId)
      if (!challenge) continue

      // Get entry count
      const entries = mockDb.findWhere<TrackRecordEntry>(
        'trackRecordEntries',
        (e) => e.trackRecordId === tr.id
      )

      pendingReviews.push({
        trackRecordId: tr.id,
        userId: tr.userId,
        challengeId: tr.challengeId,
        communityId: assignment.communityId,
        communityName: community.name,
        assignmentId: assignment.id,
        student: {
          id: student.id,
          displayName: student.displayName,
          avatarUrl: student.avatarUrl,
        },
        challenge: {
          id: challenge.id,
          title: challenge.title,
          coverImageUrl: challenge.coverImageUrl,
        },
        submittedAt: tr.submittedAt || tr.updatedAt,
        verificationAttempts: userChallenge.verificationAttempts,
        entryCount: entries.length,
      })
    }

    // Sort by submission date, oldest first
    pendingReviews.sort((a, b) => new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime())

    return success(pendingReviews)
  },

  /**
   * Get a track record for review (with full entries)
   */
  async getTrackRecordForReview(trackRecordId: UUID): Promise<ApiResponse<TrackRecord | null>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const trackRecord = mockDb.findById<TrackRecord>('trackRecords', trackRecordId)

    // Handle mock track record IDs (generated for demo students without real DB data)
    if (!trackRecord && trackRecordId.startsWith('mock-tr-')) {
      const challengeIdx = trackRecordId.indexOf('challenge-')
      const mockChallengeId = challengeIdx >= 0 ? trackRecordId.slice(challengeIdx) : ''
      const challenge = mockDb.findById<Challenge>('challenges', mockChallengeId)

      const mockEntryTexts = [
        'Started gathering all the materials I need. Really excited to get going on this!',
        'Made some good progress today. Learned a lot about the process and took notes.',
        'Hit a small roadblock but figured out a workaround. Added some photos of what I have so far.',
        'Almost done! Just need to add the finishing touches and document everything.',
      ]

      const entryCount = 2 + (trackRecordId.charCodeAt(trackRecordId.length - 1) % 3)
      const mockTR: TrackRecord = {
        id: trackRecordId,
        userChallengeId: 'mock-uc',
        userId: 'mock',
        challengeId: mockChallengeId,
        status: 'draft',
        storageUsedBytes: 0,
        sharingScope: 'communities',
        verificationAttempts: 0,
        escalated: false,
        createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date().toISOString(),
        challenge: challenge ?? undefined,
        entries: mockEntryTexts.slice(0, entryCount).map((text, i) => ({
          id: `${trackRecordId}-entry-${i}`,
          trackRecordId,
          textContent: text,
          displayOrder: i,
          editedAfterVerification: false,
          thumbsUpCount: i + 1,
          commentCount: i,
          media: [
            { id: `${trackRecordId}-m-${i}-a`, entryId: `${trackRecordId}-entry-${i}`, mediaType: 'image' as const, url: `https://picsum.photos/seed/${500 + i * 2}/600/400`, displayOrder: 0, createdAt: '', updatedAt: '' },
            { id: `${trackRecordId}-m-${i}-b`, entryId: `${trackRecordId}-entry-${i}`, mediaType: 'image' as const, url: `https://picsum.photos/seed/${501 + i * 2}/600/400`, displayOrder: 1, createdAt: '', updatedAt: '' },
          ],
          createdAt: new Date(Date.now() - (3 - i) * 24 * 60 * 60 * 1000).toISOString(),
          updatedAt: new Date(Date.now() - (3 - i) * 24 * 60 * 60 * 1000).toISOString(),
        })),
      }

      return success(mockTR)
    }

    if (!trackRecord) {
      return error('Track record not found')
    }

    // Verify the teacher has permission to review this track record
    const userChallenge = mockDb.findById<UserChallenge>('userChallenges', trackRecord.userChallengeId)
    if (!userChallenge || !userChallenge.communityId) {
      return error('Not authorized to review this track record')
    }

    const membership = mockDb.findOne<CommunityMembership>(
      'communityMemberships',
      (m) => m.communityId === userChallenge.communityId &&
             m.userId === userId &&
             m.status === 'active' &&
             ['owner', 'creator', 'admin', 'moderator'].includes(m.role)
    )
    if (!membership) {
      return error('Not authorized to review this track record')
    }

    // Populate entries
    trackRecord.entries = mockDb.findWhere<TrackRecordEntry>(
      'trackRecordEntries',
      (e) => e.trackRecordId === trackRecord.id
    ).sort((a, b) => a.displayOrder - b.displayOrder)

    // Populate media for each entry
    trackRecord.entries = trackRecord.entries.map((entry) => ({
      ...entry,
      media: mockDb.findWhere<TrackRecordEntryMedia>(
        'trackRecordMedia',
        (m) => m.entryId === entry.id
      ).sort((a, b) => a.displayOrder - b.displayOrder),
    }))

    // Populate challenge
    trackRecord.challenge = mockDb.findById<Challenge>('challenges', trackRecord.challengeId) ?? undefined

    // Populate userChallenge
    trackRecord.userChallenge = userChallenge

    return success(trackRecord)
  },

  /**
   * Get rejection reason templates
   */
  async getRejectionReasonTemplates(): Promise<ApiResponse<RejectionReasonTemplate[]>> {
    await delay(50)

    const templates = mockDb.findWhere<RejectionReasonTemplate>(
      'rejectionReasonTemplates',
      (t) => t.isActive
    ).sort((a, b) => a.displayOrder - b.displayOrder)

    return success(templates)
  },

  /**
   * Verify (approve) a track record
   */
  async verifyTrackRecord(
    trackRecordId: UUID,
    reviewNote?: string
  ): Promise<ApiResponse<TrackRecord>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const trackRecord = mockDb.findById<TrackRecord>('trackRecords', trackRecordId)
    if (!trackRecord) {
      return error('Track record not found')
    }

    if (trackRecord.status !== 'submitted') {
      return error('Track record is not pending verification')
    }

    // Verify teacher permission
    const userChallenge = mockDb.findById<UserChallenge>('userChallenges', trackRecord.userChallengeId)
    if (!userChallenge || !userChallenge.communityId) {
      return error('Not authorized')
    }

    const membership = mockDb.findOne<CommunityMembership>(
      'communityMemberships',
      (m) => m.communityId === userChallenge.communityId &&
             m.userId === userId &&
             m.status === 'active' &&
             ['owner', 'creator', 'admin', 'moderator'].includes(m.role)
    )
    if (!membership) {
      return error('Not authorized')
    }

    const now = new Date().toISOString()

    // Update track record
    const updatedTrackRecord = mockDb.update<TrackRecord>('trackRecords', trackRecordId, {
      status: 'verified',
      verifiedAt: now,
      reviewerId: userId,
      reviewerRole: 'teacher',
      rejectionReasonId: undefined,
      rejectionNote: reviewNote,
    })

    // Update user challenge
    mockDb.update<UserChallenge>('userChallenges', userChallenge.id, {
      status: 'verified',
      verifiedAt: now,
    })

    // Update community student progress if exists
    const progress = mockDb.findOne<CommunityStudentProgress>(
      'studentAssignmentProgress',
      (p) => p.userId === trackRecord.userId && p.assignmentId === userChallenge.communityAssignmentId
    )
    if (progress) {
      mockDb.update<CommunityStudentProgress>('studentAssignmentProgress', progress.id, {
        status: 'completed',
        completedAt: now,
        progressPercent: 100,
      })
    }

    // Update assignment completed count
    if (userChallenge.communityAssignmentId) {
      const assignment = mockDb.findById<CommunityAssignment>('communityAssignments', userChallenge.communityAssignmentId)
      if (assignment) {
        mockDb.update<CommunityAssignment>('communityAssignments', assignment.id, {
          completedCount: assignment.completedCount + 1,
          submittedCount: Math.max(0, assignment.submittedCount - 1),
        })
      }
    }

    if (!updatedTrackRecord) {
      return error('Failed to verify track record')
    }

    return success(updatedTrackRecord)
  },

  /**
   * Reject a track record with reason
   */
  async rejectTrackRecord(
    trackRecordId: UUID,
    data: { rejectionReasonId: UUID; rejectionNote?: string }
  ): Promise<ApiResponse<TrackRecord>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const trackRecord = mockDb.findById<TrackRecord>('trackRecords', trackRecordId)
    if (!trackRecord) {
      return error('Track record not found')
    }

    if (trackRecord.status !== 'submitted') {
      return error('Track record is not pending verification')
    }

    // Verify teacher permission
    const userChallenge = mockDb.findById<UserChallenge>('userChallenges', trackRecord.userChallengeId)
    if (!userChallenge || !userChallenge.communityId) {
      return error('Not authorized')
    }

    const membership = mockDb.findOne<CommunityMembership>(
      'communityMemberships',
      (m) => m.communityId === userChallenge.communityId &&
             m.userId === userId &&
             m.status === 'active' &&
             ['owner', 'creator', 'admin', 'moderator'].includes(m.role)
    )
    if (!membership) {
      return error('Not authorized')
    }

    // Update track record
    const updatedTrackRecord = mockDb.update<TrackRecord>('trackRecords', trackRecordId, {
      status: 'rejected',
      rejectionReasonId: data.rejectionReasonId,
      rejectionNote: data.rejectionNote,
      reviewerId: userId,
      reviewerRole: 'teacher',
    })

    // Update user challenge
    mockDb.update<UserChallenge>('userChallenges', userChallenge.id, {
      status: 'rejected',
    })

    // Update community student progress if exists - back to in_progress
    const progress = mockDb.findOne<CommunityStudentProgress>(
      'studentAssignmentProgress',
      (p) => p.userId === trackRecord.userId && p.assignmentId === userChallenge.communityAssignmentId
    )
    if (progress) {
      mockDb.update<CommunityStudentProgress>('studentAssignmentProgress', progress.id, {
        status: 'in_progress',
        progressPercent: 50,
      })
    }

    // Update assignment counts
    if (userChallenge.communityAssignmentId) {
      const assignment = mockDb.findById<CommunityAssignment>('communityAssignments', userChallenge.communityAssignmentId)
      if (assignment) {
        mockDb.update<CommunityAssignment>('communityAssignments', assignment.id, {
          submittedCount: Math.max(0, assignment.submittedCount - 1),
          inProgressCount: assignment.inProgressCount + 1,
        })
      }
    }

    if (!updatedTrackRecord) {
      return error('Failed to reject track record')
    }

    return success(updatedTrackRecord)
  },

  // ============================================================================
  // SLA Tracking Functions
  // ============================================================================

  /**
   * Get track records approaching or past SLA deadline
   */
  async getSlaPendingReviews(): Promise<ApiResponse<TrackRecord[]>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Get all submitted track records
    const submittedRecords = mockDb.findWhere<TrackRecord>(
      'trackRecords',
      (tr) => tr.status === 'submitted' && !!tr.slaDeadline
    )

    const now = new Date()

    // Filter to records within 24 hours of SLA or past SLA
    const slaPendingRecords = submittedRecords.filter((tr) => {
      if (!tr.slaDeadline) return false
      const deadline = new Date(tr.slaDeadline)
      const hoursRemaining = (deadline.getTime() - now.getTime()) / (1000 * 60 * 60)
      // Include if within 24 hours of deadline or past deadline
      return hoursRemaining <= 24
    })

    // Sort by deadline (most urgent first)
    slaPendingRecords.sort((a, b) => {
      const deadlineA = a.slaDeadline ? new Date(a.slaDeadline).getTime() : 0
      const deadlineB = b.slaDeadline ? new Date(b.slaDeadline).getTime() : 0
      return deadlineA - deadlineB
    })

    return success(slaPendingRecords)
  },

  /**
   * Check and escalate overdue track records
   * This would typically be called by a scheduled job
   */
  async checkAndEscalateSla(): Promise<ApiResponse<{ escalatedCount: number }>> {
    await delay(100)

    const now = new Date()
    let escalatedCount = 0

    // Get all submitted track records past SLA
    const overdueRecords = mockDb.findWhere<TrackRecord>(
      'trackRecords',
      (tr) =>
        tr.status === 'submitted' &&
        !!tr.slaDeadline &&
        tr.escalated === false &&
        new Date(tr.slaDeadline) < now
    )

    // Escalate each overdue record
    for (const tr of overdueRecords) {
      mockDb.update<TrackRecord>('trackRecords', tr.id, {
        escalated: true,
        escalatedAt: now.toISOString(),
      })
      escalatedCount++

      // In real implementation, this would:
      // 1. Send notification to admin/supervisor
      // 2. Create an escalation ticket
      // 3. Log the SLA breach for reporting
      console.log(`SLA Escalated: Track Record ${tr.id}, User ${tr.userId}`)
    }

    return success({ escalatedCount })
  },

  /**
   * Get SLA status for a track record
   */
  async getSlaStatus(trackRecordId: UUID): Promise<ApiResponse<{
    status: 'on_track' | 'at_risk' | 'overdue' | 'escalated' | 'not_applicable'
    hoursRemaining?: number
    deadline?: string
    attemptsRemaining: number
  }>> {
    await delay(50)

    const trackRecord = mockDb.findById<TrackRecord>('trackRecords', trackRecordId)
    if (!trackRecord) {
      return error('Track record not found')
    }

    // Not applicable if not submitted
    if (trackRecord.status !== 'submitted') {
      return success({
        status: 'not_applicable',
        attemptsRemaining: 3 - (trackRecord.verificationAttempts || 0),
      })
    }

    // Already escalated
    if (trackRecord.escalated) {
      return success({
        status: 'escalated',
        deadline: trackRecord.slaDeadline,
        attemptsRemaining: 3 - (trackRecord.verificationAttempts || 0),
      })
    }

    // No SLA deadline set
    if (!trackRecord.slaDeadline) {
      return success({
        status: 'not_applicable',
        attemptsRemaining: 3 - (trackRecord.verificationAttempts || 0),
      })
    }

    const now = new Date()
    const deadline = new Date(trackRecord.slaDeadline)
    const hoursRemaining = (deadline.getTime() - now.getTime()) / (1000 * 60 * 60)

    let status: 'on_track' | 'at_risk' | 'overdue'
    if (hoursRemaining < 0) {
      status = 'overdue'
    } else if (hoursRemaining <= 24) {
      status = 'at_risk'
    } else {
      status = 'on_track'
    }

    return success({
      status,
      hoursRemaining: Math.max(0, hoursRemaining),
      deadline: trackRecord.slaDeadline,
      attemptsRemaining: 3 - (trackRecord.verificationAttempts || 0),
    })
  },

  // ============================================================================
  // Emoji Reactions
  // ============================================================================

  async toggleReaction(
    entryId: UUID,
    emoji: EntryReactionEmoji
  ): Promise<ApiResponse<{ reactions: EntryReactionSummary; userReactions: EntryReactionEmoji[] }>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) return error('Not authenticated')

    // Find all existing reactions by this user on this entry
    const existingReactions = mockDb.findWhere<TrackRecordEntryReaction>(
      'entryReactions',
      (r) => r.entryId === entryId && r.userId === userId
    )

    const alreadySelected = existingReactions.find((r) => r.emoji === emoji)

    // Remove all existing reactions by this user (single-select: one emoji at a time)
    for (const r of existingReactions) {
      mockDb.delete('entryReactions', r.id)
    }

    if (!alreadySelected) {
      // Add the new reaction (only if not deselecting the same emoji)
      mockDb.insert<TrackRecordEntryReaction>('entryReactions', {
        entryId,
        userId,
        emoji,
      } as any)
    }

    // Return updated summary
    const allReactions = mockDb.findWhere<TrackRecordEntryReaction>(
      'entryReactions',
      (r) => r.entryId === entryId
    )
    const summary: EntryReactionSummary = {}
    const userReactions: EntryReactionEmoji[] = []
    for (const r of allReactions) {
      summary[r.emoji] = (summary[r.emoji] || 0) + 1
      if (r.userId === userId) userReactions.push(r.emoji)
    }

    return success({ reactions: summary, userReactions })
  },
}
